<template lang="pug">
.BaseFormPasswordGroup.password-group(:class="`field-${color}`")
  BaseFormGroup.password-group__field(
    :id="id",
    :name="name",
    :model="model",
    :label="label",
    ref="password",
    @focus="focused = true",
    @blur="focused = false",
    autocomplete="off",
    v-bind="$attrs",
    :class="{'is--progress-line': visibleProgressLiner, 'is--visible-password-icon': visibleShowPasswordIcon}",
    :type="type"
  )
  v-progress-linear(
    v-if="value && visibleProgressLiner"
    :value="progressValue",
    :color="color",
    height="1",
    background-color="#87919C",
  )
  span.validation-text(v-if="value && visibleProgressLiner")
    span.small-text(:class="`text-${color}`", :key="color")
      | {{ validationText }}
  .type-button(
    v-if="model.password && visibleShowPasswordIcon"
    @click="showHidePassword"
  )
    SolidYeSlashIcon.svg-icon(v-if="type === 'password'")
    SolidEyeIcon.svg-icon(v-else)
</template>

<script>
import { VProgressLinear } from 'vuetify/lib';
import SolidYeSlashIcon from 'images/svg-icons/eye-slash.svg';
import SolidEyeIcon from 'images/svg-icons/eye.svg';

export default {
  name: 'BaseFormPasswordGroup',
  components: { VProgressLinear, SolidYeSlashIcon, SolidEyeIcon },
  inheritAttrs: false,
  props: {
    id: String,
    model: {
      type: Object,
      required: true,
    },
    name: {
      type: String,
      default: 'password',
    },
    label: {
      type: String,
      default: 'Password',
    },
    visibleShowPasswordIcon: Boolean,
    visibleProgressLiner: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      focused: false,
      type: 'password',
    };
  },
  computed: {
    value() {
      return this.model.password;
    },
    hasNumber() {
      return /\d/g.test(this.value);
    },
    hasLowerLetter() {
      return /[a-z]/g.test(this.value);
    },
    hasUpperLetter() {
      return /[A-Z]/g.test(this.value);
    },
    hasSpecialCharacter() {
      return /[!@#$%^&*(),.?":{}|<>]/g.test(this.value);
    },
    isLow() {
      return this.value.length >= 8;
    },
    groupFitNumber() {
      let number = 0;
      number += Number(this.hasNumber);
      number += Number(this.hasLowerLetter);
      number += Number(this.hasUpperLetter);
      number += Number(this.hasSpecialCharacter);
      return number;
    },
    isGood() {
      return (this.isLow && this.groupFitNumber >= 3) || this.value.length >= 12;
    },
    isExcellent() {
      return this.isGood && this.groupFitNumber === 4;
    },
    validationText() {
      if (this.isExcellent) return 'Excellent';
      if (this.isGood) return 'Good';
      return 'Weak';
    },
    color() {
      if (!this.visibleProgressLiner) return 'default';
      if (this.isExcellent) return 'accent';
      if (this.isGood) return 'accent';
      return 'error';
    },
    progressValue() {
      if (this.isExcellent) return 100;
      if (this.isGood) return 70;
      if (this.isLow) return 40;
      return Math.min(100, this.value.length * 5);
    },
  },
  methods: {
    showHidePassword() {
      if (this.type === 'password') this.type = 'text';
      else this.type = 'password';
    },
  },
};
</script>

<style lang="sass" scoped>
@import '~Styles/_variables'
.password-group
  position: relative
  margin-bottom: 15px
  &__field
    &.is--visible-password-icon
      ::v-deep .v-text-field__slot
        padding-right: 35px
    &.is--progress-line
      ::v-deep .v-input
        &.v-input--is-focused
          fieldset
            border-color: transparent !important
            &:before
              opacity: 1
  ::v-deep .v-input__slot
    margin-bottom: 0 !important
  ::v-deep .v-input
    .v-text-field__details
      position: absolute
      width: 100%
      left: 0
      bottom: -20px

  ::v-deep .base-form-group--error
    margin-bottom: 30px
  ::v-deep .v-progress-linear
    position: absolute
    left: 0
    bottom: 0
    width: calc(100% - 2px)
    margin-bottom: 0
    margin-top: 0
    z-index: 2
    &__background
      border-radius: 0
    &__bar__determinate
      border-radius: 0
    &__determinate
      &.error
        background-color: $support_red
.validation-text
  position: absolute
  left: 0
  bottom: -20px
  .check-circle-icon
    margin-left: 3px
    height: sizeInRem(12)
    width: sizeInRem(12)
    fill: $secondary
  .text
    &-error
      color: $support_red
    &-sienna
      color: $secondary
    &-accent
      color: $secondary
.type-button
  position: absolute
  right: 15px
  top: 43px
  z-index: 2
  cursor: pointer
  .svg-icon
    width: 20px
    height: 20px
    fill: $text_gray
</style>
